import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://store.comparehomeupgrades.com/api';
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // a wrong password on the login form is also a 401 — that must not reload the page
    if (error.response?.status === 401 && !error.config?.url?.endsWith('/auth/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  me: () => api.get('/auth/me'),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }),
  listUsers: () => api.get('/auth/users'),
  createUser: (data) => api.post('/auth/users', data),
  updateUser: (id, data) => api.put('/auth/users/' + id, data),
  resetPassword: (id, new_password) => api.post('/auth/users/' + id + '/reset-password', { new_password }),
  deleteUser: (id) => api.delete('/auth/users/' + id),
};

// Cross-store stock sync (admin)
export const stockSyncAPI = {
  getSettings: () => api.get('/stock-sync/settings'),
  saveSettings: (data) => api.put('/stock-sync/settings', data),
  saveSelected: (master_skus) => api.put('/stock-sync/selected', { master_skus }),
  getActivity: (limit = 100) => api.get('/stock-sync/activity', { params: { limit } }),
  runNow: () => api.post('/stock-sync/run'),
  registerShopifyWebhook: () => api.post('/stock-sync/shopify-webhook/register'),
};

// COGS rates per Article + Style (monthly reports)
export const cogsAPI = {
  list: () => api.get('/sku-mapping/cogs-rates'),
  save: (rates) => api.put('/sku-mapping/cogs-rates', { rates }),
  stores: () => api.get('/sku-mapping/cogs-rates/stores'),
  importPreview: (scope, rows) => api.post('/sku-mapping/cogs-rates/import/preview', { scope, rows }, { timeout: 300000 }),
  importApply: (scope, rows, recalc_from) => api.post('/sku-mapping/cogs-rates/import/apply', { scope, rows, recalc_from }, { timeout: 600000 }),
};

// Monthly report data (the Excel files are built in the browser)
export const monthlyReportAPI = {
  amazon: (month) => api.get('/monthly-report/amazon', { params: { month }, timeout: 300000 }),
  shopify: (month) => api.get('/monthly-report/shopify', { params: { month }, timeout: 300000 }),
  amazonSync: (month) => api.post('/amazon-transactions/all/sync', { month }),
  amazonSyncStatus: () => api.get('/amazon-transactions/sync/status'),
};

// Products API
export const productsAPI = {
  getAll: () => api.get('/products'),
  getOne: (id) => api.get(`/products/${id}`),
  sync: () => api.post('/products/sync'),
  updateCost: (id, costPrice) => api.put(`/products/${id}/cost`, { cost_price: costPrice }),
  updateInventory: (id, quantity) => api.put(`/products/${id}/inventory`, { quantity })
};

// Orders API
export const ordersAPI = {
  getAll: (params) => api.get('/orders', { params }),
  sync: (dateRange) => api.post('/orders/sync', dateRange),
  getStats: (params) => api.get('/orders/stats', { params })
};

// Reports API
export const reportsAPI = {
  getDashboard: () => api.get('/reports/dashboard'),
  getProfitReport: (params) => api.get('/reports/profit', { params }),
  getProductReport: (params) => api.get('/reports/products', { params }),
  exportOrders: (params) => api.get('/reports/export/orders', { 
    params,
    responseType: 'blob'
  })
};

export default api;